import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import moment from 'moment';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useShiftsForCalendarWeek } from '~/hooks/api/useShiftsForCalendarWeek';
import { shiftDetailQueryOptions } from '~/hooks/api/useShiftDetails';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import type { ShiftWithTurns, FilteredShift } from './utils';
import {
  filterShifts,
  filterShiftsAppliedByPublisher,
  getPublishersWithMultipleAssignments,
  getUnassignedApplicants,
} from './utils';
import type { SidebarView } from './PublisherSidebar';

const EMPTY_RESULT = {
  filteredShiftsByDay: new Map<number, FilteredShift[]>(),
  maxShifts: 0,
} as const;
import DayColumn from './DayColumn';
import PublisherSidebar from './PublisherSidebar';

const route = getRouteApi('/weekplan/$year/$week/overview');

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export const WeeklyOverview = () => {
  const params = route.useParams();
  const search = route.useSearch();
  const navigate = useNavigate();
  const year = parseInt(params.year, 10);
  const week = parseInt(params.week, 10);
  const highlightPublisherId = search.highlightPublisher ?? null;
  const sidebarView: SidebarView = search.sidebarView ?? 'assigned';

  const shiftsQuery = useShiftsForCalendarWeek({ year, week });

  const shiftIds = useMemo(
    () =>
      (shiftsQuery.data ?? [])
        .map((s) => s.id)
        .filter((id): id is number => id != null),
    [shiftsQuery.data]
  );

  const shiftDetailQueries = useQueries({
    queries: shiftIds.map((id) => ({
      ...shiftDetailQueryOptions(id),
      enabled: shiftIds.length > 0,
    })),
  });

  const shiftDetailData = shiftDetailQueries.map((q) => q.data);

  const shiftDetailMap = useMemo(() => {
    const map = new Map<number, ShiftWithTurns['detailedTurns']>();
    for (const data of shiftDetailData) {
      if (data) {
        map.set(data.id, data.turns ?? []);
      }
    }
    return map;
  }, [shiftDetailData]);

  const weekStart = useMemo(
    () => moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek'),
    [year, week]
  );

  const isApplicantHighlight =
    sidebarView === 'applied' && highlightPublisherId != null;

  const { filteredShiftsByDay, maxShifts } = useMemo(() => {
    const dayShifts = new Map<number, ShiftWithTurns[]>();
    if (!shiftsQuery.data) return EMPTY_RESULT;

    for (const shift of shiftsQuery.data) {
      if (shift.id == null) continue;
      const dayOfWeek = moment(shift.startDate).isoWeekday();
      const existing = dayShifts.get(dayOfWeek) ?? [];
      existing.push({
        ...shift,
        id: shift.id,
        detailedTurns: shiftDetailMap.get(shift.id),
      });
      dayShifts.set(dayOfWeek, existing);
    }

    const filtered = new Map<number, FilteredShift[]>();
    let max = 0;
    for (const [day, shifts] of dayShifts) {
      shifts.sort(
        (a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf()
      );
      const result = isApplicantHighlight
        ? filterShiftsAppliedByPublisher(shifts, highlightPublisherId)
        : filterShifts(shifts);
      filtered.set(day, result);
      max = Math.max(max, result.length);
    }
    return { filteredShiftsByDay: filtered, maxShifts: max };
  }, [
    shiftsQuery.data,
    shiftDetailMap,
    isApplicantHighlight,
    highlightPublisherId,
  ]);

  const handlePublisherClick = (publisherId: number) => {
    navigate({
      to: '/weekplan/$year/$week/overview',
      params: { year: year.toString(), week: week.toString() },
      search: {
        highlightPublisher:
          highlightPublisherId === publisherId ? undefined : publisherId,
        sidebarView,
      },
      replace: true,
    });
  };

  const handleViewChange = (view: SidebarView) => {
    navigate({
      to: '/weekplan/$year/$week/overview',
      params: { year: year.toString(), week: week.toString() },
      search: {
        highlightPublisher: undefined,
        sidebarView: view,
      },
      replace: true,
    });
  };

  const publishersWithMultiple = useMemo(
    () => getPublishersWithMultipleAssignments(filteredShiftsByDay),
    [filteredShiftsByDay]
  );

  const unassignedApplicants = useMemo(
    () => getUnassignedApplicants(shiftsQuery.data ?? []),
    [shiftsQuery.data]
  );

  const isLoading =
    shiftsQuery.isLoading ||
    (shiftIds.length > 0 && shiftDetailQueries.some((q) => q.isLoading));

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        flex: 1,
        minHeight: 0,
      }}
    >
      {(publishersWithMultiple.length > 0 ||
        unassignedApplicants.length > 0) && (
        <Box
          sx={{
            flexShrink: 0,
            width: 220,
            display: { xs: 'none', md: 'block' },
          }}
        >
          <PublisherSidebar
            view={sidebarView}
            multipleAssignments={publishersWithMultiple}
            unassignedApplicants={unassignedApplicants}
            selectedPublisherId={highlightPublisherId}
            onPublisherClick={handlePublisherClick}
            onViewChange={handleViewChange}
          />
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(4, 1fr)',
            },
            columnGap: 0,
            rowGap: 0,
          }}
        >
          {WEEKDAYS.map((dayNumber) => (
            <DayColumn
              key={dayNumber}
              dayDate={weekStart.clone().isoWeekday(dayNumber)}
              filteredShifts={filteredShiftsByDay.get(dayNumber) ?? []}
              maxShifts={maxShifts}
              highlightPublisherId={
                isApplicantHighlight ? null : highlightPublisherId
              }
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default WeeklyOverview;
